import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import useEntityEditor from "./useEntityEditor";
import { getUserLoginHistory } from "../repository/data.repository";
import { QueryOperation } from "../code/search-query/search-query";
import { MAX_PAGE_SIZE } from "../repository/data.default";

export type UserInputMode =
  | "assignReward"
  | "scheduleEvent"
  | "resetPassword"
  | "confirmEmail"
  | "confirmPhone"
  | "confirmCompanyPhone"
  | "lock"
  | "unlock";

export interface UseUserProfileCardOptions {
  uid?: string | null;
  open?: boolean;
  getFn: (uid: string) => Promise<any>;
  updateFn: (input: any) => Promise<any>;
  /** Load + aggregate the 90-day login-activity history (corporate cards). */
  withHistory?: boolean;
}

/**
 * Shared scaffolding for every user-profile card (the ~80% that all five views duplicated):
 * load the entity (+ optional login history), the `useEntityEditor` with the dateOfBirth
 * transform, and the action-dialog open/close state. Each view supplies only its get/update fns.
 */
export function useUserProfileCard(opts: UseUserProfileCardOptions) {
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState<Record<string, any> | null>(null);
  const [history, setHistory] = useState<{ [key: string]: number }>({});
  const [error, setError] = useState(false);
  const [inputMode, setInputMode] = useState<UserInputMode>("assignReward");
  const [inputOpen, setInputOpen] = useState(false);

  // Editor.onSuccess needs loadData, which is defined below — bridge via a ref to avoid TDZ.
  const loadRef = useRef<() => void>(() => {});

  const editor = useEntityEditor({
    item,
    updateFn: opts.updateFn,
    onSuccess: () => loadRef.current(),
    toFormValues: (i) => ({
      ...i,
      dateOfBirth: i.dateOfBirth ? dayjs(i.dateOfBirth) : undefined,
    }),
    fromFormValues: (v) => ({
      ...v,
      dateOfBirth: v.dateOfBirth ? v.dateOfBirth.format("YYYY-MM-DD") : undefined,
    }),
  });

  function loadData(): void {
    if (!opts.uid) return;
    setLoading(true);
    const tasks: Promise<any>[] = [opts.getFn(opts.uid)];
    if (opts.withHistory) {
      tasks.push(
        getUserLoginHistory({
          searchQuery: {
            conditions: [
              { field: "userUid", operation: QueryOperation.Equal, values: [opts.uid] },
              {
                field: "timestamp",
                operation: QueryOperation.Between,
                values: [
                  dayjs().subtract(90, "days").format("YYYY-MM-DDTHH:mm:ssZ"),
                  dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
                ],
              },
            ],
          },
          pageNumber: 0,
          pageSize: MAX_PAGE_SIZE,
        }),
      );
    }
    Promise.all(tasks)
      .then((results) => {
        setLoading(false);
        setItem(results[0]);
        if (opts.withHistory && results[1]) {
          const agg = results[1].data.reduce((acc: any, h: any) => {
            const d = dayjs(h.timestamp).format("YYYY-MM-DD");
            acc[d] = (acc[d] ?? 0) + 1;
            return acc;
          }, {} as { [key: string]: number });
          setHistory(agg);
        }
      })
      .catch(() => setLoading(false));
  }
  loadRef.current = loadData;

  function reset(): void {
    setError(false);
    setLoading(false);
    setItem(null);
    setHistory({});
  }

  useEffect(() => {
    if (opts.open) loadData();
    else reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.open, opts.uid]);

  const onInputOpen = (mode: UserInputMode): void => {
    setInputMode(mode);
    setInputOpen(true);
  };
  const onInputClose = (): void => setInputOpen(false);
  const onInputFinish = (): void => {
    setInputOpen(false);
    loadData();
  };

  const data = item as Record<string, any> | null;

  return {
    loading,
    setLoading,
    item,
    data,
    history,
    error,
    setError,
    editor,
    inputMode,
    inputOpen,
    onInputOpen,
    onInputClose,
    onInputFinish,
    reload: loadData,
  };
}

export default useUserProfileCard;
